/**
 * Utilities for checking and verifying SharedArrayBuffer availability
 * Required for Zoom Video SDK advanced features and performance improvements
 */

/**
 * Check if SharedArrayBuffer is available in the current environment
 * @returns {boolean} True if SharedArrayBuffer is supported and enabled
 */
export function isSharedArrayBufferAvailable() {
  return typeof SharedArrayBuffer === 'function';
}

/**
 * Check if cross-origin isolation is enabled
 * @returns {boolean} True if the page is cross-origin isolated
 */
export function isCrossOriginIsolated() {
  return typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated;
}

/**
 * Get detailed SharedArrayBuffer status information
 * @returns {object} Object containing detailed status information
 */
export function getSharedArrayBufferStatus() {
  const sharedArrayBufferSupported = isSharedArrayBufferAvailable();
  const crossOriginIsolation = isCrossOriginIsolated();

  return {
    sharedArrayBufferSupported,
    crossOriginIsolation,
    environment: import.meta.env.VITE_ENVIRONMENT || 'unknown',
    enableShareArrayBuffer: import.meta.env.VITE_ENABLE_SHAREDARRAYBUFFER === 'true',
    ready: sharedArrayBufferSupported && crossOriginIsolation,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  };
}

/**
 * Log SharedArrayBuffer status to console for debugging
 * Only logs in development or when explicitly enabled
 */
export function logSharedArrayBufferStatus() {
  const isDevelopment = import.meta.env.DEV;
  const shouldLog = isDevelopment || import.meta.env.VITE_ENABLE_SHAREDARRAYBUFFER === 'true';

  if (!shouldLog) return;

  const status = getSharedArrayBufferStatus();

  console.group('🔧 SharedArrayBuffer Status');
  console.log('Environment:', status.environment);
  console.log('SharedArrayBuffer Enabled in Config:', status.enableShareArrayBuffer);
  console.log('SharedArrayBuffer Supported:', status.sharedArrayBufferSupported ? '✅' : '❌');
  console.log('Cross-Origin Isolation:', status.crossOriginIsolation ? '✅' : '❌');
  console.log('Ready for Zoom SDK:', status.ready ? '✅' : '❌');
  console.log('User Agent:', status.userAgent);
  console.log('Timestamp:', status.timestamp);

  if (!status.ready) {
    console.warn('⚠️ SharedArrayBuffer is not available. Zoom Video SDK may have limited functionality.');

    if (!status.sharedArrayBufferSupported) {
      console.warn('- SharedArrayBuffer API is not supported by this browser');
    }

    if (!status.crossOriginIsolation) {
      console.warn('- Cross-Origin Isolation is not enabled. Check HTTP headers:');
      console.warn('  • Cross-Origin-Embedder-Policy: credentialless (or require-corp)');
      console.warn('  • Cross-Origin-Opener-Policy: same-origin');
    }
  }

  console.groupEnd();
}

/**
 * Initialize SharedArrayBuffer checking and logging
 * Should be called once during app initialization
 */
export function initializeSharedArrayBufferCheck() {
  // Log status on initialization
  logSharedArrayBufferStatus();

  // Set up periodic status checking in development
  if (import.meta.env.DEV) {
    // Check again after page load to ensure headers are properly applied
    setTimeout(() => {
      const status = getSharedArrayBufferStatus();
      if (!status.ready) {
        console.warn('🔧 SharedArrayBuffer still not ready after page load');
        logSharedArrayBufferStatus();
      }
    }, 1000);
  }
}

/**
 * Verify if Zoom Video SDK can use SharedArrayBuffer
 * @returns {Promise<object>} Promise resolving to verification result
 */
export async function verifyZoomSharedArrayBufferCompatibility() {
  const status = getSharedArrayBufferStatus();

  try {
    // Try to create a small SharedArrayBuffer as a test
    if (status.sharedArrayBufferSupported) {
      const testBuffer = new SharedArrayBuffer(16);
      const testArray = new Int32Array(testBuffer);
      testArray[0] = 42;

      return {
        ...status,
        testPassed: testArray[0] === 42,
        zoomCompatible: status.ready,
        message: status.ready ?
          'SharedArrayBuffer is ready for Zoom Video SDK' :
          'SharedArrayBuffer is not available - Zoom Video SDK will use fallback mode'
      };
    }

    return {
      ...status,
      testPassed: false,
      zoomCompatible: false,
      message: 'SharedArrayBuffer is not supported by this browser'
    };

  } catch (error) {
    return {
      ...status,
      testPassed: false,
      zoomCompatible: false,
      error: error.message,
      message: `SharedArrayBuffer test failed: ${error.message}`
    };
  }
}